import type { ActionItem } from '../types/ai';

/**
 * Format a Date or SQL Datetime string ('2026-07-28 14:30:00' or '2026-07-28')
 * to Google Calendar compact ISO format: YYYYMMDDTHHMMSS
 */
export function formatCompactIsoDate(dateInput: string | Date, fallbackAddHours = 0): string {
  let date: Date;

  if (typeof dateInput === 'string') {
    // Handle 'YYYY-MM-DD HH:mm:ss' or 'YYYY-MM-DD'
    const normalizedStr = dateInput.replace(' ', 'T');
    date = new Date(normalizedStr);
    if (isNaN(date.getTime())) {
      date = new Date();
    }
  } else {
    date = new Date(dateInput);
  }

  if (fallbackAddHours > 0) {
    date = new Date(date.getTime() + fallbackAddHours * 60 * 60 * 1000);
  }

  const pad = (n: number) => String(n).padStart(2, '0');

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());

  return `${year}${month}${day}T${hours}${minutes}${seconds}`;
}

/**
 * Generates a Google Calendar creation URL from an ActionItem
 */
export function buildGoogleCalendarUrl(action: ActionItem, contactName?: string): string {
  const title = action.title || action.description || 'Compromisso Agendado';

  let startCompact: string;
  let endCompact: string;

  if (action.event_start_time) {
    startCompact = formatCompactIsoDate(action.event_start_time);
    if (action.event_end_time) {
      endCompact = formatCompactIsoDate(action.event_end_time);
    } else {
      endCompact = formatCompactIsoDate(action.event_start_time, 1); // +1 hour fallback
    }
  } else if (action.due_date) {
    // Default to 09:00 - 10:00 on due_date
    const baseDateStr = `${action.due_date}T09:00:00`;
    startCompact = formatCompactIsoDate(baseDateStr);
    endCompact = formatCompactIsoDate(baseDateStr, 1);
  } else {
    const now = new Date();
    startCompact = formatCompactIsoDate(now);
    endCompact = formatCompactIsoDate(now, 1);
  }

  const datesParam = `${startCompact}/${endCompact}`;

  let detailsText = `Compromisso registrado via Alfred Executivo AI.`;
  if (action.assignee) {
    detailsText += `\nResponsável: ${action.assignee}`;
  }
  if (contactName) {
    detailsText += `\nContato WhatsApp: ${contactName}`;
  }

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: datesParam,
    details: detailsText,
    location: action.location || '',
    ctz: 'America/Sao_Paulo'
  });

  return `https://www.google.com/calendar/render?${params.toString()}`;
}
